import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, Star } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Account = { id: number; name: string }

type Contact = {
  id: number
  first_name: string
  last_name: string
  full_name: string
  email: string
  phone: string
  mobile: string
  job_title: string
  account: number | null
  account_name: string | null
  is_primary: boolean
  is_active: boolean
}

// Minimal create payload — rest filled in on the record page.
type CreateInput = {
  first_name: string
  last_name: string
  email: string
  phone: string
  job_title: string
  account: number | null
  is_primary: boolean
}

export default function CRMContacts() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const contactsQ = useQuery({
    queryKey: ['crm', 'contacts'],
    queryFn: async () => (await api.get<Contact[]>('/crm/contacts/')).data,
  })
  const accountsQ = useQuery({
    queryKey: ['crm', 'accounts'],
    queryFn: async () => (await api.get<Account[]>('/crm/accounts/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filterActive, setFilterActive] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ACTIVE')

  function openCreate() {
    setDraft(emptyDraft())
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Contact>('/crm/contacts/', {
      ...draft,
      mobile: '',
      notes: '',
      owner: null,
      is_active: true,
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['crm', 'contacts'] })
      toast.success('Contact created')
      setCreating(false)
      navigate(`/admin/crm/contacts/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/crm/contacts/${id}/`),
    onSuccess: () => {
      toast.success('Contact deleted')
      qc.invalidateQueries({ queryKey: ['crm', 'contacts'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const contacts = contactsQ.data || []
  const accounts = accountsQ.data || []
  const filtered = filterActive === 'ALL' ? contacts
    : filterActive === 'ACTIVE' ? contacts.filter((c) => c.is_active)
    : contacts.filter((c) => !c.is_active)

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Contacts</h2>
          <p className="text-sm text-muted-foreground">
            {contacts.length} total · {contacts.filter((c) => c.is_active).length} active
          </p>
        </div>
        <Button onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> New contact</Button>
      </div>

      <div className="mb-4 flex items-center space-x-2 text-sm">
        <span className="text-muted-foreground">Show:</span>
        {(['ALL', 'ACTIVE', 'INACTIVE'] as const).map((s) => (
          <button key={s} onClick={() => setFilterActive(s)}
            className={`px-3 py-1 rounded ${filterActive === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {contactsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">No contacts.</div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Name</Th>
                <Th>Account</Th>
                <Th>Job title</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th className="text-center">Primary</Th>
                <Th className="text-center">Active</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/crm/contacts/${c.id}`)}
                >
                  <Td className="font-medium">{c.full_name}</Td>
                  <Td>{c.account_name || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-xs">{c.job_title || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-xs">{c.email || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-xs">{c.phone || c.mobile || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-center">
                    {c.is_primary && <Star className="h-4 w-4 fill-amber-400 text-amber-500 inline" />}
                  </Td>
                  <Td className="text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${c.is_active ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/crm/contacts/${c.id}`)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => confirm(
                            `contact ${c.full_name}`,
                            () => deleteMut.mutate(c.id),
                          )}
                          className="text-destructive"
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create drawer — minimal fields. Mobile + notes on the record page. */}
      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New contact"
        description="Capture the essentials. Mobile, notes, and primary toggle live on the record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="ct-first">First name *</FieldLabel>
              <Input id="ct-first" autoFocus required value={draft.first_name}
                onChange={(ev) => setDraft({ ...draft, first_name: ev.target.value })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="ct-last">Last name</FieldLabel>
              <Input id="ct-last" value={draft.last_name}
                onChange={(ev) => setDraft({ ...draft, last_name: ev.target.value })} />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="ct-job">Job title</FieldLabel>
            <Input id="ct-job" value={draft.job_title}
              onChange={(ev) => setDraft({ ...draft, job_title: ev.target.value })} />
          </Field>

          <Field>
            <FieldLabel htmlFor="ct-account">Account</FieldLabel>
            <SearchableSelect
              id="ct-account"
              value={draft.account ? String(draft.account) : NONE}
              onChange={(v) => setDraft({ ...draft, account: v === NONE ? null : Number(v) })}
              placeholder="— none —"
              options={[
                { value: NONE, label: '— none —' },
                ...accounts.map((a) => ({ value: String(a.id), label: a.name })),
              ]}
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="ct-email">Email</FieldLabel>
              <Input id="ct-email" type="email" value={draft.email}
                onChange={(ev) => setDraft({ ...draft, email: ev.target.value })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="ct-phone">Phone</FieldLabel>
              <Input id="ct-phone" type="tel" value={draft.phone}
                onChange={(ev) => setDraft({ ...draft, phone: ev.target.value })} />
            </Field>
          </div>

          <Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.is_primary}
                onChange={(ev) => setDraft({ ...draft, is_primary: ev.target.checked })} />
              Primary contact for this account
            </label>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return {
    first_name: '', last_name: '', email: '', phone: '', job_title: '',
    account: null, is_primary: false,
  }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
